/* Meine-Holzbox - Gestufte Geschenke und Versandvorteile im Warenkorb
   Gehoert zu snippets/cart-reward-tiers.liquid
   Schwellen, Geschenke und Farben werden dort gepflegt, nicht hier. */
import Cart from "cart-store";

import { formatMoney } from "utils";

const MAX_SYNC_ATTEMPTS = 4;

/* Modulweite Sperre: die Leiste haengt zweimal im Dokument (Popup und
   Warenkorbseite). Ohne die Sperre wuerden beide gleichzeitig Geschenke
   nachlegen wollen. */
let syncLock = false;

/* Ebenfalls modulweit: welche Geschenke wir auf dieser Seite schon gelegt haben.
   Beide Leisten teilen sich den Stand, sonst legt die eine wieder rein, was die
   Kundin gerade bei der anderen entfernt hat. */
const addedByUs = new Set();

/* Das Theme aktualisiert den Gesamtpreis im Kasse-Button nur an einer Stelle:
   in seinem eigenen Store-Abo, mit dem zuletzt eingetroffenen Warenkorb. Weil
   wir pro Klick eine zweite Warenkorb-Anfrage schicken, koennen die Antworten
   in falscher Reihenfolge ankommen und der Button bleibt auf einem alten Wert
   stehen. Deshalb schreiben wir die Summe zusaetzlich selbst und holen nach
   jeder Warenkorb-Bewegung einmal den verbindlichen Stand nach. */
const TOTAL_REFRESH_DELAY = 600;
let refreshTimer = null;
let refreshInFlight = false;

function paintCartTotals(cart) {
  if (!cart || typeof cart.total_price !== "number") return;
  const formatted = formatMoney(cart.total_price, window.theme.money_total_price_format);
  document.querySelectorAll("cart-total").forEach((element) => {
    if (element.innerHTML !== formatted) element.innerHTML = formatted;
  });
}

/* Einmal, nachdem sich der Warenkorb beruhigt hat: verbindlichen Stand holen
   und die Summe schreiben. Damit kann kein veralteter Betrag stehenbleiben,
   egal in welcher Reihenfolge die Antworten eintrudeln. */
function scheduleAuthoritativeRefresh() {
  if (refreshInFlight) return;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(async () => {
    refreshTimer = null;
    refreshInFlight = true;
    try {
      const cart = await Cart.refresh();
      paintCartTotals(cart);
    } catch (e) {
      console.warn("[reward-tiers] Warenkorb konnte nicht aufgefrischt werden:", e);
    } finally {
      refreshInFlight = false;
    }
  }, TOTAL_REFRESH_DELAY);
}

function declineKey(productId) {
  return `mh-reward-declined-${productId}`;
}

function isDeclined(productId) {
  try {
    return window.localStorage.getItem(declineKey(productId)) === "1";
  } catch (e) {
    return false;
  }
}

function setDeclined(productId, declined) {
  try {
    if (declined) {
      window.localStorage.setItem(declineKey(productId), "1");
    } else {
      window.localStorage.removeItem(declineKey(productId));
    }
  } catch (e) {
    /* Privater Modus - dann eben ohne Merken. */
  }
}

class RewardTiers extends HTMLElement {
  #unsubscribe = null;

  #attempts = 0;

  connectedCallback() {
    const configElement = this.querySelector('script[type="application/json"]');
    if (!configElement) return;

    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (e) {
      return;
    }

    this.tiers = this.config.tiers || [];
    if (!this.tiers.length) return;

    this.maxThreshold = this.tiers[this.tiers.length - 1].threshold;
    this.giftVariantIds = [];
    this.tiers.forEach((tier) => {
      if (!tier.gift) return;
      tier.gift.colors.forEach((color) => {
        this.giftVariantIds.push(color.plain);
        if (color.engraved) this.giftVariantIds.push(color.engraved);
      });
    });

    this.messageElement = this.querySelector("[data-reward-message]");
    this.fillElement = this.querySelector("[data-reward-fill]");
    this.tierElements = Array.from(this.querySelectorAll("[data-reward-tier]"));
    this.giftElements = Array.from(this.querySelectorAll("[data-reward-gift]"));

    this.#bindControls();

    const initialCart = Cart.getState().cart || {};
    this.#render(initialCart);
    this.#sync(initialCart);

    this.#unsubscribe = Cart.subscribe((state) => {
      const cart = state.cart || {};
      this.#render(cart);
      this.#sync(cart);
      scheduleAuthoritativeRefresh();
    });
  }

  disconnectedCallback() {
    if (this.#unsubscribe) this.#unsubscribe();
  }

  #tierForElement(element) {
    const index = Number(element.getAttribute("data-reward-gift"));
    return { index: index, tier: this.tiers[index] };
  }

  #bindControls() {
    this.giftElements.forEach((element) => {
      const { tier } = this.#tierForElement(element);
      if (!tier || !tier.gift) return;

      element.querySelectorAll("[data-reward-color]").forEach((button) => {
        button.addEventListener("click", () => {
          if (button.disabled) return;
          const choice = this.#choiceFor(tier);
          this.#choose(tier, button.getAttribute("data-reward-color"), choice.engraved);
        });
      });

      const engrave = element.querySelector("[data-reward-engrave]");
      if (engrave) {
        engrave.addEventListener("change", () => {
          const choice = this.#choiceFor(tier);
          this.#choose(tier, choice.colorName, engrave.checked);
        });
      }
    });
  }

  /* Warenkorbwert ohne die Geschenke selbst - sonst wuerde der Gravuraufpreis
     den Fortschritt mit hochziehen.
     Zaehlt den Betrag NACH Gutschein: Codes wie INSTA15 oder BACK20 wirken auf
     den ganzen Warenkorb und stehen nicht in final_line_price. Shopify verteilt
     sie anteilig auf alle Zeilen, also ziehen wir den Anteil ab, der auf die
     Nicht-Geschenke faellt. Muss zur Rechnung im Snippet passen. */
  #qualifyingTotal(cart) {
    let qualifying = 0;
    let gifts = 0;

    (cart.items || []).forEach((item) => {
      const price = item.final_line_price ?? item.line_price ?? 0;
      if (this.giftVariantIds.includes(item.variant_id)) {
        gifts += price;
      } else {
        qualifying += price;
      }
    });

    const cartDiscount = (cart.cart_level_discount_applications || []).reduce(
      (sum, application) => {
        if (application.target_type && application.target_type !== "line_item") return sum;
        return sum + (Number(application.total_allocated_amount) || 0);
      },
      0
    );

    if (cartDiscount <= 0 || qualifying <= 0) return qualifying;

    const share = Math.round((cartDiscount * qualifying) / (qualifying + gifts));
    return Math.max(0, qualifying - share);
  }

  /* Jede Stufe bekommt gleich viel Balken. Muss zur Rechnung im Snippet passen. */
  #progressPercent(total) {
    const count = this.tiers.length;
    const step = 100 / count;
    let lower = 0;

    for (let index = 0; index < count; index += 1) {
      const upper = this.tiers[index].threshold;
      if (total < upper) {
        return index * step + ((total - lower) / (upper - lower)) * step;
      }
      lower = upper;
    }

    return 100;
  }

  #giftLines(cart, tier) {
    if (!tier.gift) return [];
    const ids = [];
    tier.gift.colors.forEach((color) => {
      ids.push(color.plain);
      if (color.engraved) ids.push(color.engraved);
    });
    return (cart.items || []).filter((item) => ids.includes(item.variant_id));
  }

  #colorFor(tier, variantId) {
    return tier.gift.colors.find(
      (color) => color.plain === variantId || color.engraved === variantId
    );
  }

  #storageKey(tier) {
    return `mh-reward-choice-${tier.gift.productId}`;
  }

  /* Was gerade gilt: bevorzugt das, was im Warenkorb liegt, sonst die zuletzt
     gewaehlte Farbe, sonst die Voreinstellung aus dem Snippet. */
  #choiceFor(tier) {
    const cart = Cart.getState().cart || {};
    const lines = this.#giftLines(cart, tier);

    if (lines.length) {
      const color = this.#colorFor(tier, lines[0].variant_id);
      if (color) {
        return { colorName: color.name, engraved: color.engraved === lines[0].variant_id };
      }
    }

    /* Nur die Farbe wird gemerkt, nie die Gravur: die kostet extra und muss
       nach deutschem Recht jedes Mal aktiv angehakt werden. Kein vorangekreuztes
       Kaestchen fuer eine kostenpflichtige Zusatzleistung. */
    try {
      const stored = JSON.parse(window.localStorage.getItem(this.#storageKey(tier)) || "null");
      if (stored && stored.colorName) return { colorName: stored.colorName, engraved: false };
    } catch (e) {
      /* Privater Modus - dann eben ohne Merken. */
    }

    return { colorName: tier.gift.defaultColor, engraved: false };
  }

  #rememberChoice(tier, choice) {
    try {
      window.localStorage.setItem(
        this.#storageKey(tier),
        JSON.stringify({ colorName: choice.colorName })
      );
    } catch (e) {
      /* Privater Modus - dann eben ohne Merken. */
    }
  }

  #variantFor(tier, colorName, engraved) {
    const colors = tier.gift.colors;
    let color = colors.find((entry) => entry.name === colorName) || colors[0];

    if (!engraved) return { variantId: color.plain, color: color, engraved: false };

    /* Nicht jede Farbe ist gravierbar - dann nehmen wir die erste, die es ist. */
    if (!color.engraved) {
      color = colors.find((entry) => entry.engraved) || color;
    }

    return color.engraved
      ? { variantId: color.engraved, color: color, engraved: true }
      : { variantId: color.plain, color: color, engraved: false };
  }

  async #choose(tier, colorName, engraved) {
    const target = this.#variantFor(tier, colorName, engraved);
    this.#rememberChoice(tier, { colorName: target.color.name, engraved: target.engraved });

    /* Wer aktiv eine Farbe waehlt, will das Geschenk - auch wenn es vorher
       einmal entfernt wurde. */
    setDeclined(tier.gift.productId, false);
    addedByUs.add(tier.gift.productId);

    const cart = Cart.getState().cart || {};
    const lines = this.#giftLines(cart, tier);
    const alreadyRight = lines.length === 1 && lines[0].variant_id === target.variantId;

    this.#renderGifts(cart, { tier: tier, override: target });
    if (alreadyRight) return;

    const updates = {};
    lines.forEach((line) => {
      updates[line.variant_id] = 0;
    });
    updates[target.variantId] = 1;

    syncLock = true;
    try {
      const result = await Cart.update({ updates: updates }, "rewardTiers");
      paintCartTotals(result);
    } catch (e) {
      console.warn("[reward-tiers] Auswahl konnte nicht übernommen werden:", e);
    } finally {
      syncLock = false;
      scheduleAuthoritativeRefresh();
    }
  }

  #render(cart) {
    const total = this.#qualifyingTotal(cart);

    paintCartTotals(cart);

    if (this.fillElement) this.fillElement.style.width = `${this.#progressPercent(total)}%`;
    this.setAttribute("value", total);
    this.toggleAttribute("data-all-reached", total >= this.maxThreshold);

    this.tierElements.forEach((element) => {
      const index = Number(element.getAttribute("data-reward-tier"));
      const tier = this.tiers[index];
      if (!tier) return;
      element.classList.toggle("is-reached", total >= tier.threshold);
    });

    this.#renderMessage(total);
    this.#renderGifts(cart);
  }

  #renderMessage(total) {
    if (!this.messageElement) return;

    const nextTier = this.tiers.find((tier) => total < tier.threshold);
    let message;

    if (total === 0) {
      message = this.config.messages.empty;
    } else if (!nextTier) {
      message = this.config.messages.complete;
    } else {
      const missing = formatMoney(nextTier.threshold - total, window.theme.money_format).replace(
        /[.,]00$/,
        ""
      );
      message = nextTier.message.replace("[amount]", `<strong>${missing}</strong>`);
    }

    if (this.messageElement.innerHTML !== message) this.messageElement.innerHTML = message;
  }

  #renderGifts(cart, options) {
    const total = this.#qualifyingTotal(cart);

    this.giftElements.forEach((element) => {
      const { tier } = this.#tierForElement(element);
      if (!tier || !tier.gift) return;

      const unlocked = total >= tier.threshold && total > 0;
      element.toggleAttribute("hidden", !unlocked);
      if (!unlocked) return;

      let choice;
      if (options && options.tier === tier && options.override) {
        choice = { colorName: options.override.color.name, engraved: options.override.engraved };
      } else {
        choice = this.#choiceFor(tier);
      }

      const engrave = element.querySelector("[data-reward-engrave]");
      if (engrave) engrave.checked = choice.engraved;

      const note = element.querySelector("[data-reward-note]");
      if (note) note.toggleAttribute("hidden", !choice.engraved);

      /* Ueberschrift sagt immer, was gerade drinliegt: "ohne Gravur / gratis"
         oder "mit Gravur / +3 EUR". */
      const labels = tier.gift.labels;
      if (labels) {
        const suffix = element.querySelector("[data-reward-gift-engraving]");
        if (suffix) suffix.textContent = choice.engraved ? labels.engraved : labels.plain;

        const badge = element.querySelector("[data-reward-gift-badge]");
        if (badge) badge.textContent = choice.engraved ? labels.engravedBadge : labels.freeBadge;
      }

      element.querySelectorAll("[data-reward-color]").forEach((button) => {
        const name = button.getAttribute("data-reward-color");
        const color = tier.gift.colors.find((entry) => entry.name === name);
        const selectable = Boolean(color) && (!choice.engraved || Boolean(color.engraved));

        button.disabled = !selectable;
        button.classList.toggle("is-blocked", !selectable);
        button.classList.toggle("is-selected", selectable && name === choice.colorName);
        button.title = selectable ? "" : tier.gift.blockedHint || "";
      });
    });
  }

  /* Legt freigeschaltete Geschenke in den Warenkorb, raeumt sie wieder heraus,
     sobald der Warenkorbwert unter die Schwelle faellt, und sorgt dafuer, dass
     pro Geschenk genau eine Variante drinliegt. */
  async #sync(cart) {
    if (syncLock || this.config.autoAdd === false) return;
    if (this.#attempts >= MAX_SYNC_ATTEMPTS) return;

    /* Solange das Theme selbst eine Zeile aendert, halten wir still. Zwei
       gleichzeitige Warenkorb-Anfragen sind genau das, was den Gesamtpreis im
       Kasse-Button durcheinanderbringt. Das naechste Store-Update ruft uns
       ohnehin wieder auf. */
    const busy = Cart.getState().lineItemsBeingUpdated;
    if (busy && busy.length) return;

    const total = this.#qualifyingTotal(cart);
    const updates = {};
    let changed = false;

    this.tiers.forEach((tier) => {
      if (!tier.gift) return;

      const lines = this.#giftLines(cart, tier);
      const qualifies = total > 0 && total >= tier.threshold;

      if (!qualifies) {
        addedByUs.delete(tier.gift.productId);
        setDeclined(tier.gift.productId, false);
        lines.forEach((line) => {
          updates[line.variant_id] = 0;
          changed = true;
        });
        return;
      }

      if (lines.length === 0) {
        /* Fehlt das Geschenk, obwohl wir es schon einmal gelegt hatten, hat es
           die Kundin bewusst entfernt. Das respektieren wir. */
        if (addedByUs.has(tier.gift.productId)) {
          setDeclined(tier.gift.productId, true);
          return;
        }
        if (isDeclined(tier.gift.productId)) return;

        const choice = this.#choiceFor(tier);
        const target = this.#variantFor(tier, choice.colorName, choice.engraved);
        updates[target.variantId] = 1;
        changed = true;
        addedByUs.add(tier.gift.productId);
        return;
      }

      addedByUs.add(tier.gift.productId);

      /* Mehrere Varianten desselben Geschenks oder Menge groesser eins: aufraeumen. */
      lines.forEach((line, index) => {
        if (index === 0) {
          if (line.quantity !== 1) {
            updates[line.variant_id] = 1;
            changed = true;
          }
          return;
        }
        updates[line.variant_id] = 0;
        changed = true;
      });
    });

    if (!changed) return;

    syncLock = true;
    this.#attempts += 1;

    try {
      const result = await Cart.update({ updates: updates }, "rewardTiers");
      paintCartTotals(result);
      this.#attempts = 0;
    } catch (e) {
      console.warn("[reward-tiers] Geschenke konnten nicht aktualisiert werden:", e);
    } finally {
      syncLock = false;
      scheduleAuthoritativeRefresh();
    }
  }
}

if (!customElements.get("reward-tiers")) {
  customElements.define("reward-tiers", RewardTiers);
}